(ns rothko.editor.matcher
  "Protocol used is from https://github.com/nubank/matcher-combinators
  That library has issues in cljs around dispatch which is not
  surprising as it's focus is on testing."
  (:require [clojure.set :refer [subset?]]
            [medley.core :refer [find-first remove-vals]]))

(defprotocol Matcher
  (match [this actual]
    "determine if a concrete `actual` value satisfies this matcher"))

(defrecord AnyMatcher []
  Matcher
  (match [_ _]
    true))

(def ?any (->AnyMatcher))

(defrecord AbsentMatcher []
  Matcher
  (match [_ _]
    (throw (ex-info "Do not call directly" {}))))

(def absent (->AbsentMatcher))

(defrecord NotMatcher [expected]
  Matcher
  (match [_ actual]
    (not (match expected actual))))

(def not-matcher ->NotMatcher)

(defrecord AndMatcher [expected]
  Matcher
  (match [_ actual]
    (->> expected
         (reduce (fn [thus-far this] (and thus-far (match this actual))) true))))

(defn and-matcher
  [& expected]
  (->AndMatcher expected))

(defrecord OrMatcher [expected]
  Matcher
  (match [_ actual]
    (->> expected
         (reduce (fn [thus-far this] (or thus-far (match this actual))) false))))

(defn one-of [& expected] (->OrMatcher expected))

(defrecord FnMatcher [f]
  Matcher
  (match [_ actual]
    (boolean (f actual))))

(defn fn-matcher
  [f]
  (->FnMatcher f))

(defrecord EveryMatcher [expected]
  Matcher
  (match [_ actual] (every? #(match expected %) actual)))
(defn every [expected] (->EveryMatcher expected))

(defrecord StartsWithMatcher [expected]
  Matcher
  (match [_ actual]
    (match expected (subvec
                     actual
                     0
                     (min (count actual) (count expected))))))

(defrecord EndsWithMatcher [expected]
  Matcher
  (match [_ actual]
    (match expected (subvec
                     actual
                     (max 0
                          (- (count actual) (count expected)))
                     (count actual)))))

(defn starts-with
  [expected]
  (->StartsWithMatcher expected))

(defn ends-with
  [expected]
  (->EndsWithMatcher expected))

(defn map-values-matcher
  [matcher actual]
  (reduce-kv
   (fn [acc matcher-key matcher-value]
     (and acc (if (= absent matcher-value)
                (not (contains? actual matcher-key))
                (match matcher-value (get actual matcher-key)))))
   true
   matcher))

(defn subset-map-matcher
  [matcher actual]
  (and (subset? (set (keys (remove-vals #(= absent %) matcher)))
                (set (keys actual)))
       (map-values-matcher matcher actual)))

(defn seq-matcher
  [matcher actual]
  (and (= (count matcher)
          (count actual))
       (every? true? (doall (map match matcher actual)))))

(defn exact-map-matcher
  [matcher actual]
  (and (= (set (keys matcher))
          (set (keys actual)))
       (map-values-matcher matcher actual)))

(defn set-contains-matcher
  [matcher actual]
  (contains? matcher actual))

(defn value-matcher
  [matcher actual]
  (= matcher actual))

(defn regex-matcher
  [matcher actual]
  (boolean (re-matches matcher actual)))

(extend-protocol Matcher

  #?(:clj clojure.lang.PersistentArrayMap
     :cljs cljs.core/PersistentArrayMap)
  (match [this actual]
    (subset-map-matcher this actual))

  #?(:clj clojure.lang.PersistentTreeMap
     :cljs cljs.core/PersistentTreeMap)
  (match [this actual]
    (subset-map-matcher this actual))

  #?(:clj clojure.lang.PersistentHashMap
     :cljs cljs.core/PersistentHashMap)
  (match [this actual]
    (subset-map-matcher this actual))

  #?(:clj clojure.lang.Keyword
     :cljs cljs.core/Keyword)
  (match [this actual]
    (= this actual))

  #?(:clj clojure.lang.IPersistentSet
     :cljs cljs.core/PersistentHashSet)
  (match [this actual]
    (set-contains-matcher this actual))

  #?(:clj clojure.lang.IPersistentVector
     :cljs cljs.core/PersistentVector)
  (match [this actual]
    (seq-matcher this actual))

  #?(:clj clojure.lang.IPersistentList
     :cljs cljs.core/List)
  (match [this actual]
    (seq-matcher this actual))

  #?(:clj java.lang.String
     :cljs string)
  (match [this actual]
    (value-matcher this actual))

  #?(:clj java.util.regex.Pattern
     :cljs js/RegExp)
  (match [this actual]
    (regex-matcher this actual))

  #?(:clj Boolean
     :cljs boolean)
  (match [this actual]
    (value-matcher this actual)))

(defn match?
  [matcher expected]
  (match matcher expected))

(defn dispatch
  [defmulti-var value]
  (let [dispatch-values (keys (methods defmulti-var))]
    (or (find-first #(match? % value) dispatch-values)
        value)))
